import { useRef, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../utils/api'
import {
  ClipboardList, ArrowLeft, Printer, Trash2, Plus, Check, MapPin, User, CalendarClock,
  Monitor, Building2, Upload, RefreshCw, PenLine, CheckCircle2, XCircle, Paperclip, File, ImageIcon,
  ChevronUp, ChevronDown, Pencil, StickyNote, X,
} from 'lucide-react'
import RichTextEditor from '../components/RichTextEditor'
import { AssetSearchSelect } from './TicketsPage'
import WorkOrderSignatureBox, { WorkOrderSignatureData } from '../components/WorkOrderSignatureBox'

interface Item {
  id: string; description: string; isChecked: boolean; checkedAt: string | null; checkedBy: string | null; note: string | null
}
interface Attachment {
  id: string; filename: string; originalName: string; url: string; size: number | null; mimeType: string | null
}
interface WorkOrder {
  id: string; workOrderNumber: number; title: string; customerName: string; location: string | null
  status: string; description: string | null; scheduledDate: string | null; createdBy: string | null
  completedAt: string | null; signedAt: string | null; createdAt: string
  asset: { id: string; assetCode: string | null; name: string } | null
  company: { id: string; name: string } | null
  items: Item[]
  signatures: WorkOrderSignatureData[]
  attachments: Attachment[]
}

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'ร่าง', IN_PROGRESS: 'กำลังดำเนินการ', COMPLETED: 'เสร็จงาน', SIGNED: 'ลงนามแล้ว', CANCELLED: 'ยกเลิก',
}
const STATUS_COLOR: Record<string, string> = {
  DRAFT:        'bg-gray-50 text-gray-600 border-gray-200',
  IN_PROGRESS:  'bg-blue-50 text-blue-700 border-blue-200',
  COMPLETED:    'bg-amber-50 text-amber-700 border-amber-200',
  SIGNED:       'bg-green-50 text-green-700 border-green-200',
  CANCELLED:    'bg-red-50 text-red-600 border-red-200',
}

export default function WorkOrderDetailPage({ canManageAssets }: { canManageAssets: boolean }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [newItem, setNewItem] = useState('')
  const [editingItemId, setEditingItemId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [noteValue, setNoteValue] = useState('')
  const attachRef = useRef<HTMLInputElement>(null)
  const [uploadingAttachment, setUploadingAttachment] = useState(false)
  const [showEdit, setShowEdit] = useState(false)

  const { data: wo, isLoading } = useQuery<WorkOrder>({
    queryKey: ['workorder', id],
    queryFn: () => api.get(`/workorders/${id}`).then(r => r.data),
    enabled: !!id,
  })

  const toggleMut = useMutation({
    mutationFn: (itemId: string) => api.patch(`/workorders/${id}/items/${itemId}/toggle`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workorder', id] }),
  })
  const addItemMut = useMutation({
    mutationFn: (description: string) => api.post(`/workorders/${id}/items`, { description }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['workorder', id] }); setNewItem('') },
  })
  const deleteItemMut = useMutation({
    mutationFn: (itemId: string) => api.delete(`/workorders/${id}/items/${itemId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workorder', id] }),
  })
  const editItemMut = useMutation({
    mutationFn: ({ itemId, description }: { itemId: string; description: string }) =>
      api.put(`/workorders/${id}/items/${itemId}`, { description }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['workorder', id] }); setEditingItemId(null) },
  })
  const editNoteMut = useMutation({
    mutationFn: ({ itemId, note }: { itemId: string; note: string }) =>
      api.put(`/workorders/${id}/items/${itemId}`, { note }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['workorder', id] }); setEditingNoteId(null) },
  })
  const reorderItemsMut = useMutation({
    mutationFn: (itemIds: string[]) => api.patch(`/workorders/${id}/items/reorder`, { itemIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workorder', id] }),
  })

  const startEdit = (item: Item) => { setEditingItemId(item.id); setEditValue(item.description) }
  const saveEdit = () => {
    if (!editingItemId) return
    const trimmed = editValue.trim()
    if (!trimmed) { setEditingItemId(null); return }
    editItemMut.mutate({ itemId: editingItemId, description: trimmed })
  }
  const startEditNote = (item: Item) => { setEditingNoteId(item.id); setNoteValue(item.note || '') }
  const saveEditNote = () => {
    if (!editingNoteId) return
    editNoteMut.mutate({ itemId: editingNoteId, note: noteValue.trim() })
  }
  const moveItem = (index: number, dir: -1 | 1) => {
    if (!wo) return
    const target = index + dir
    if (target < 0 || target >= wo.items.length) return
    const ids = wo.items.map(i => i.id)
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    reorderItemsMut.mutate(ids)
  }
  const statusMut = useMutation({
    mutationFn: (status: string) => api.patch(`/workorders/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workorder', id] }),
  })
  const deleteWoMut = useMutation({
    mutationFn: () => api.delete(`/workorders/${id}`),
    onSuccess: () => navigate('/workorders'),
  })
  const deleteAttachmentMut = useMutation({
    mutationFn: (attachmentId: string) => api.delete(`/workorders/${id}/attachments/${attachmentId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workorder', id] }),
    onError: (e: any) => alert(e.response?.data?.message || 'ลบไฟล์ไม่สำเร็จ'),
  })
  const uploadAttachments = async (files: FileList | null) => {
    if (!files?.length) return
    setUploadingAttachment(true)
    const fd = new FormData()
    Array.from(files).forEach(f => fd.append('files', f))
    try {
      await api.post(`/workorders/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      qc.invalidateQueries({ queryKey: ['workorder', id] })
    } catch (e: any) {
      alert(e.response?.data?.message || 'อัปโหลดไม่สำเร็จ')
    } finally { setUploadingAttachment(false) }
  }

  if (isLoading) return <div className="flex items-center justify-center h-64 text-gray-400">กำลังโหลด...</div>
  if (!wo) return <div className="flex items-center justify-center h-64 text-gray-400">ไม่พบใบงาน</div>

  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('th-TH', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
  const fmtDateTime = (d: string | null) => d ? new Date(d).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' }) : '—'
  const doneCount = wo.items.filter(i => i.isChecked).length
  const progress = wo.items.length ? Math.round((doneCount / wo.items.length) * 100) : 0
  const isLocked = wo.status === 'SIGNED' || wo.status === 'CANCELLED'

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={() => navigate('/workorders')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
          <ArrowLeft size={15}/> กลับไปที่รายการใบงาน
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => window.open(`/workorders/${id}/print`, '_blank')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200">
            <Printer size={15}/> พิมพ์ใบงาน
          </button>
          {canManageAssets && wo.status !== 'SIGNED' && (
            <button onClick={() => setShowEdit(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-xl text-sm font-medium hover:bg-blue-100">
              <Pencil size={15}/> แก้ไข
            </button>
          )}
          {canManageAssets && wo.status === 'DRAFT' && (
            <button onClick={() => { if (confirm('ยืนยันลบใบงานนี้?')) deleteWoMut.mutate() }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-xl text-sm font-medium hover:bg-red-100">
              <Trash2 size={15}/> ลบ
            </button>
          )}
        </div>
      </div>

      {/* Header card */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <span className="font-mono text-xs text-gray-400">#{wo.workOrderNumber}</span>
            <h1 className="text-lg font-bold text-gray-900">{wo.title}</h1>
          </div>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${STATUS_COLOR[wo.status]}`}>{STATUS_LABEL[wo.status] || wo.status}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 text-sm">
          <div className="flex items-center gap-2 text-gray-600"><User size={14} className="text-gray-400"/> {wo.customerName}</div>
          {wo.location && <div className="flex items-center gap-2 text-gray-600"><MapPin size={14} className="text-gray-400"/> {wo.location}</div>}
          <div className="flex items-center gap-2 text-gray-600"><CalendarClock size={14} className="text-gray-400"/> นัดหมาย {fmtDate(wo.scheduledDate)}</div>
          {wo.company && <div className="flex items-center gap-2 text-gray-600"><Building2 size={14} className="text-gray-400"/> {wo.company.name}</div>}
          {wo.asset && (
            <Link to={`/assets/${wo.asset.id}`} className="flex items-center gap-2 text-blue-600 hover:underline">
              <Monitor size={14}/> {wo.asset.assetCode || wo.asset.name}
            </Link>
          )}
        </div>
        {wo.description && <div className="kb-content text-sm text-gray-600 mt-3 border-t border-gray-50 pt-3" style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: wo.description }}/>}

        {canManageAssets && !isLocked && (
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            {wo.status !== 'COMPLETED' && (
              <button onClick={() => statusMut.mutate('COMPLETED')} className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-100">
                <CheckCircle2 size={13}/> ทำเครื่องหมายว่าเสร็จงาน
              </button>
            )}
            <button onClick={() => { if (confirm('ยืนยันยกเลิกใบงานนี้?')) statusMut.mutate('CANCELLED') }} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100">
              <XCircle size={13}/> ยกเลิกใบงาน
            </button>
          </div>
        )}
      </div>

      {/* Checklist */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2"><ClipboardList size={16}/> รายการงาน (Checklist)</h2>
          <span className="text-xs text-gray-400">{doneCount}/{wo.items.length} เสร็จแล้ว</span>
        </div>
        {wo.items.length > 0 && (
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
            <div className="h-full bg-green-500 transition-all" style={{ width: `${progress}%` }}/>
          </div>
        )}

        <div className="space-y-1.5">
          {wo.items.map((item, index) => (
            <div key={item.id} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50 group">
              <button
                disabled={isLocked}
                onClick={() => toggleMut.mutate(item.id)}
                className={`w-5 h-5 mt-0.5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors disabled:opacity-50 ${item.isChecked ? 'bg-green-500 border-green-500' : 'border-gray-300 hover:border-green-400'}`}>
                {item.isChecked && <Check size={13} className="text-white"/>}
              </button>
              <div className="flex-1 min-w-0">
                {editingItemId === item.id ? (
                  <input
                    autoFocus
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditingItemId(null) }}
                    onBlur={saveEdit}
                    className="w-full border border-blue-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                ) : (
                  <p
                    onClick={() => canManageAssets && !isLocked && startEdit(item)}
                    className={`text-sm ${item.isChecked ? 'text-gray-400 line-through' : 'text-gray-800'} ${canManageAssets && !isLocked ? 'cursor-text hover:bg-gray-100 rounded px-1 -mx-1' : ''}`}>
                    {item.description}
                  </p>
                )}
                {item.isChecked && item.checkedAt && (
                  <p className="text-xs text-gray-400 mt-0.5">✓ {item.checkedBy || '—'} · {fmtDateTime(item.checkedAt)}</p>
                )}
                {editingNoteId === item.id ? (
                  <input
                    autoFocus
                    value={noteValue}
                    onChange={e => setNoteValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEditNote(); if (e.key === 'Escape') setEditingNoteId(null) }}
                    onBlur={saveEditNote}
                    placeholder="หมายเหตุ..."
                    className="w-full mt-1 border border-blue-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                ) : item.note ? (
                  <p
                    onClick={() => canManageAssets && !isLocked && startEditNote(item)}
                    className={`text-xs text-gray-500 mt-0.5 flex items-center gap-1 ${canManageAssets && !isLocked ? 'cursor-text hover:bg-gray-100 rounded px-1 -mx-1' : ''}`}>
                    <StickyNote size={11} className="flex-shrink-0"/> {item.note}
                  </p>
                ) : (
                  canManageAssets && !isLocked && (
                    <button onClick={() => startEditNote(item)} className="text-xs text-gray-300 hover:text-gray-500 mt-0.5 opacity-0 group-hover:opacity-100">
                      + เพิ่มหมายเหตุ
                    </button>
                  )
                )}
              </div>
              {canManageAssets && !isLocked && (
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 flex-shrink-0">
                  <button disabled={index === 0} onClick={() => moveItem(index, -1)} className="p-1 text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-300">
                    <ChevronUp size={14}/>
                  </button>
                  <button disabled={index === wo.items.length - 1} onClick={() => moveItem(index, 1)} className="p-1 text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-300">
                    <ChevronDown size={14}/>
                  </button>
                  <button onClick={() => startEdit(item)} className="p-1 text-gray-300 hover:text-blue-500">
                    <Pencil size={14}/>
                  </button>
                  <button onClick={() => deleteItemMut.mutate(item.id)} className="p-1 text-gray-300 hover:text-red-500">
                    <Trash2 size={14}/>
                  </button>
                </div>
              )}
            </div>
          ))}
          {wo.items.length === 0 && <p className="text-sm text-gray-400 text-center py-4">ยังไม่มีรายการงาน</p>}
        </div>

        {canManageAssets && !isLocked && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-50">
            <input value={newItem} onChange={e => setNewItem(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && newItem.trim()) addItemMut.mutate(newItem.trim()) }}
              placeholder="เพิ่มรายการงาน..." className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            <button disabled={!newItem.trim() || addItemMut.isPending} onClick={() => addItemMut.mutate(newItem.trim())}
              className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"><Plus size={16}/></button>
          </div>
        )}
      </div>

      {/* Attachments */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-3"><Paperclip size={16}/> รูปภาพ / ไฟล์แนบหน้างาน</h2>
        <div className="space-y-2 mb-3">
          {wo.attachments.map(att => (
            <div key={att.id} className="flex items-center gap-2 bg-gray-50 rounded-lg hover:bg-gray-100">
              <a href={att.url} target="_blank" rel="noreferrer" className="flex-1 min-w-0 flex items-center gap-2 p-2 text-sm text-gray-700">
                {att.mimeType === 'application/pdf' ? <File size={14} className="text-red-400 shrink-0"/> : <ImageIcon size={14} className="text-blue-400 shrink-0"/>}
                <span className="truncate">{att.originalName}</span>
              </a>
              {canManageAssets && !isLocked && (
                <button
                  onClick={() => { if (confirm(`ลบไฟล์ "${att.originalName}"?`)) deleteAttachmentMut.mutate(att.id) }}
                  disabled={deleteAttachmentMut.isPending}
                  title="ลบไฟล์"
                  className="p-2 mr-1 text-gray-400 hover:text-red-500 disabled:opacity-50">
                  <Trash2 size={14}/>
                </button>
              )}
            </div>
          ))}
          {wo.attachments.length === 0 && <p className="text-sm text-gray-400">ยังไม่มีไฟล์แนบ</p>}
        </div>
        {!isLocked && (
          <button onClick={() => attachRef.current?.click()} disabled={uploadingAttachment}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 disabled:opacity-50">
            {uploadingAttachment ? <RefreshCw size={13} className="animate-spin"/> : <Upload size={13}/>} แนบไฟล์
          </button>
        )}
        <input ref={attachRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={e => uploadAttachments(e.target.files)}/>
      </div>

      {/* Signatures */}
      <WorkOrderSignatureBox
        workOrderId={id!}
        role="STAFF"
        label={<><PenLine size={16}/> ลายเซ็นผู้ปฏิบัติงาน</>}
        signature={wo.signatures.find(s => s.role === 'STAFF') || null}
        canManage={canManageAssets}
        isLocked={isLocked}
        fmtDateTime={fmtDateTime}
      />
      <WorkOrderSignatureBox
        workOrderId={id!}
        role="CUSTOMER"
        label={<><PenLine size={16}/> ลายเซ็นลูกค้า</>}
        signature={wo.signatures.find(s => s.role === 'CUSTOMER') || null}
        canManage={canManageAssets}
        isLocked={isLocked}
        fmtDateTime={fmtDateTime}
      />
      {showEdit && <EditModal wo={wo} onClose={() => setShowEdit(false)}/>}
    </div>
  )
}

function EditModal({ wo, onClose }: { wo: WorkOrder; onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    title: wo.title, customerName: wo.customerName, location: wo.location || '',
    assetId: wo.asset?.id || '', scheduledDate: wo.scheduledDate ? wo.scheduledDate.slice(0, 10) : '',
    description: wo.description || '', status: wo.status,
  })
  const [error, setError] = useState<string | null>(null)

  const saveMut = useMutation({
    mutationFn: () => api.put(`/workorders/${wo.id}`, {
      title: form.title.trim(), customerName: form.customerName.trim(), location: form.location,
      assetId: form.assetId, scheduledDate: form.scheduledDate, status: form.status,
      description: (form.description.replace(/<[^>]*>/g, '').trim() || /<(img|table)/.test(form.description)) ? form.description : '',
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['workorder', wo.id] }); qc.invalidateQueries({ queryKey: ['workorders'] }); onClose() },
    onError: (e: any) => setError(e?.response?.data?.message || 'บันทึกไม่สำเร็จ'),
  })

  const canSave = form.title.trim() && form.customerName.trim() && !saveMut.isPending
  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Pencil size={16}/> แก้ไขใบงาน #{wo.workOrderNumber}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {error && <div className="bg-red-50 text-red-600 text-xs px-3 py-2 rounded-lg">{error}</div>}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">หัวข้องาน *</label>
            <input className={ic} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}/>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">ลูกค้า / หน่วยงาน *</label>
            <input className={ic} value={form.customerName} onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))}/>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">สถานที่</label>
            <input className={ic} value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}/>
          </div>
          <AssetSearchSelect label="ผูกกับ Asset (ถ้ามี)" value={form.assetId} onChange={id => setForm(f => ({ ...f, assetId: id }))}/>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">วันที่นัดหมาย</label>
              <input type="date" className={ic} value={form.scheduledDate} onChange={e => setForm(f => ({ ...f, scheduledDate: e.target.value }))}/>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">สถานะ</label>
              <select className={ic} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                {['DRAFT', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'].map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">รายละเอียดงาน</label>
            <RichTextEditor
              content={form.description}
              onChange={html => setForm(f => ({ ...f, description: html }))}
              placeholder="อธิบายรายละเอียดงาน... (แทรกรูปภาพหรือตารางได้)"
              uploadEndpoint="/tickets/upload-image"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">ยกเลิก</button>
          <button onClick={() => saveMut.mutate()} disabled={!canSave}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saveMut.isPending ? 'กำลังบันทึก...' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  )
}
